import { useState } from 'react'
import { useShoppingLists } from './hooks/useShoppingLists'
import { ShoppingListView } from './components/ShoppingListView'
import { useRecipes } from '../recipes/hooks/useRecipes'
import { Spinner } from '../../components/ui/Spinner'
import { Portal } from '../../components/Portal'
import { Button } from '../../components/ui/Button'
import { SearchBar } from '../../components/ui/SearchBar'

const ListCard = ({ list, onTap, onDelete }) => {
  const [confirm, setConfirm] = useState(false)
  return (
    <div
      className="bg-surface rounded-xl border border-border p-4 flex items-center gap-3 hover:border-primary/30 transition-colors cursor-pointer active:bg-background"
      onClick={() => onTap(list)}
    >
      <div className="w-10 h-10 rounded-xl bg-primary-light flex items-center justify-center flex-shrink-0">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d1b5e" strokeWidth="2">
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-text-primary">{list.name}</h3>
      </div>
      {confirm ? (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setConfirm(false)} className="text-xs text-text-muted font-medium">Cancel</button>
          <button onClick={() => onDelete(list.id)} className="text-xs text-rose-500 font-semibold">Delete</button>
        </div>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); setConfirm(true) }}
          className="text-text-muted hover:text-rose-500 transition-colors p-1"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      )}
    </div>
  )
}

const NewListModal = ({ onCreate, onClose }) => {
  const [name,   setName]   = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    try {
      const list = await onCreate(name.trim())
      onClose(list)
    } finally { setSaving(false) }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => onClose()}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-2xl w-full max-w-sm p-6 space-y-4 shadow-card max-h-[90dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h3 className="font-bold text-text-primary text-lg">New Shopping List</h3>
        <input
          autoFocus
          type="text"
          placeholder="List name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <div className="flex gap-2">
          <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={() => onClose()}>Cancel</Button>
          <Button type="submit" size="sm" className="flex-1" disabled={saving || !name.trim()}>
            {saving ? <Spinner size="sm" /> : 'Create'}
          </Button>
        </div>
      </form>
    </div>
    </Portal>
  )
}

export const ShoppingPage = () => {
  const { data: lists = [], isLoading, createList, deleteList } = useShoppingLists()
  const { data: recipes = [] } = useRecipes()
  const [selectedList, setSelectedList] = useState(null)
  const [showNew,      setShowNew]      = useState(false)
  const [search,       setSearch]       = useState('')

  if (selectedList) {
    return (
      <ShoppingListView
        list={selectedList}
        recipes={recipes}
        onBack={() => setSelectedList(null)}
      />
    )
  }

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Shopping</h1>
          <p className="text-sm text-text-muted mt-0.5">{lists.length} list{lists.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 5v14M5 12h14" />
          </svg>
          New List
        </button>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search lists…" className="mb-4" />

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : lists.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <svg className="w-12 h-12 mx-auto mb-3 opacity-30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
          <p className="text-sm">No shopping lists yet.</p>
          <button onClick={() => setShowNew(true)} className="mt-2 text-sm text-primary font-medium">
            Create your first list →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {lists
            .filter((list) => !search.trim() || list.name?.toLowerCase().includes(search.toLowerCase()))
            .map((list) => (
              <ListCard key={list.id} list={list} onTap={setSelectedList} onDelete={deleteList} />
            ))}
        </div>
      )}

      {showNew && (
        <NewListModal
          onCreate={createList}
          onClose={(newList) => {
            setShowNew(false)
            if (newList?.id) setSelectedList(newList)
          }}
        />
      )}
    </div>
  )
}
